import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Typography,
    Box,
    Fade,
    IconButton,
    CircularProgress
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CelebrationIcon from '@mui/icons-material/Celebration';
import getIdToken from './getIdToken';

/**
 * Dialog that appears when user receives free credits
 * Checks sessionStorage for the flag and auto-dismisses after showing
 * Now fetches credits dynamically from the signup promotion plan
 */
const FreeCreditsDialog = () => {
    const [open, setOpen] = useState(false);
    const [credits, setCredits] = useState(null);
    const [loading, setLoading] = useState(false);
    const location = useLocation();

    useEffect(() => {
        // Check if user just received free credits
        const receivedFreeCredits = sessionStorage.getItem('receivedFreeCredits');

        if (receivedFreeCredits === 'true') {
            fetchSignupCredits();
        }
    }, [location]);

    const fetchSignupCredits = async () => {
        setLoading(true);
        try {
            const idToken = await getIdToken();

            // Fetch promotion plans
            const response = await fetch(
                `${process.env.REACT_APP_API_BASE_URL}xeo/v1/plans?type=promotion`,
                {
                    method: 'GET',
                    headers: {
                        Authorization: `Bearer ${idToken}`,
                        'Content-Type': 'application/json',
                    },
                }
            );

            const result = await response.json();

            // Filter for signup promotion that is active
            const signupPromotion = result.plans?.find(
                plan => plan.promotion_type === 'signup' && plan.active === true
            );

            if (signupPromotion) {
                setCredits(signupPromotion.credits);
                // Small delay to ensure page is fully loaded and rendered
                setTimeout(() => {
                    setOpen(true);
                }, 800);
            } else {
                // Fallback: if no signup promotion found, don't show dialog
                console.warn('No active signup promotion found');
                sessionStorage.removeItem('receivedFreeCredits');
            }
        } catch (error) {
            console.error('Error fetching signup credits:', error);
            // Fallback: don't show dialog on error
            sessionStorage.removeItem('receivedFreeCredits');
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => {
        setOpen(false);
        // Clear the flag so dialog doesn't show again
        sessionStorage.removeItem('receivedFreeCredits');
    };

    // Don't render anything if still loading or credits not fetched
    if (loading || credits === null) {
        return null;
    }

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="xs"
            fullWidth
            TransitionComponent={Fade}
            PaperProps={{
                sx: {
                    boxShadow: 6,
                }
            }}
        >
            <DialogTitle sx={{ textAlign: 'center', pb: 1, pt: 3 }}>
                <IconButton
                    onClick={handleClose}
                    sx={{
                        position: 'absolute',
                        right: 8,
                        top: 8,
                        color: 'var(--ink-3)'
                    }}
                >
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            <DialogContent sx={{ textAlign: 'center', px: 4, py: 2 }}>
                <Box sx={{ mb: 2 }}>
                    <CelebrationIcon
                        sx={{
                            fontSize: 64,
                            color: 'primary.main',
                            animation: 'bounce 1s ease-in-out'
                        }}
                    />
                </Box>

                <Typography
                    variant="h5"
                    component="h2"
                    gutterBottom
                    sx={{ fontWeight: 'bold', color: 'primary.main' }}
                >
                    Congratulations!
                </Typography>

                <Typography
                    variant="body1"
                    color="text.secondary"
                    sx={{ mt: 2 }}
                >
                    You have received <strong>{credits} free credits</strong> to get started!
                </Typography>
            </DialogContent>

            <DialogActions sx={{ justifyContent: 'center', pb: 3, px: 3 }}>
                <Button
                    onClick={handleClose}
                    variant="contained"
                    color="primary"
                    fullWidth
                    sx={{
                        textTransform: 'none',
                        py: 1.5,
                        fontSize: '1rem',
                        borderRadius: 2
                    }}
                >
                    Get Started
                </Button>
            </DialogActions>

            <style>
                {`
          @keyframes bounce {
            0%, 100% { transform: translateY(0); }
            50% { transform: translateY(-10px); }
          }
        `}
            </style>
        </Dialog>
    );
};

export default FreeCreditsDialog;